import React from "react";
import { Button, Typography, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { logError } from "../../utils/appUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
    padding: theme.spacing(3),
    textAlign: "center",
  },
  icon: {
    fontSize: 48,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  message: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

// Fallback component shown when an error occurs
const ErrorFallback = ({ error, resetError, componentName }) => {
  const classes = useStyles();
  
  return (
    <Paper className={classes.root} elevation={0}>
      <ErrorOutlineIcon className={classes.icon} />
      <Typography variant="h6" gutterBottom>
        Something went wrong
      </Typography>
      <Typography variant="body2" className={classes.message}>
        {componentName ? `Error in ${componentName}` : "An unexpected error occurred"}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={<RefreshIcon />}
        onClick={resetError}
        className={classes.button}
      >
        Try Again
      </Button>
    </Paper>
  );
};

/**
 * Error Boundary component that catches JavaScript errors in child components.
 * Displays a fallback UI and allows users to retry.
 * 
 * NOTE: This MUST be a class component. React Error Boundaries require
 * getDerivedStateFromError and componentDidCatch lifecycle methods which
 * are only available in class components. This is the one exception to
 * the "no classes" workspace rule.
 * 
 * Usage:
 * <ErrorBoundary componentName="TrackManagement">
 *   <TrackManagementComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const { componentName = "Unknown" } = this.props;
    
    // Log error to backend
    logError(
      `ErrorBoundary:${componentName}`,
      { 
        message: error?.message, 
        stack: error?.stack,
        componentStack: errorInfo?.componentStack 
      },
      null
    );
    
    console.error(`ErrorBoundary caught error in ${componentName}:`, error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use default
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <ErrorFallback
          error={this.state.error}
          resetError={this.resetError}
          componentName={this.props.componentName}
        />
      );
    }

    return this.props.children;
  }
}
